import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

import { isStoreBuild } from './platform';

/**
 * Тактильный отклик — короткая вибрация в ответ на событие.
 *
 * ЗАЧЕМ. Это то, чего сайт в браузере не умеет: телефон отвечает телу, а не
 * только глазам. Ученик, отвечающий в тренажёре, чувствует верно или неверно,
 * не дожидаясь, пока взгляд найдёт подсветку. Для проверки в App Store это
 * заодно один из признаков, что перед ними приложение, а не обёртка сайта.
 *
 * ПРАВИЛО, ПО КОТОРОМУ РАССТАВЛЕНЫ ВЫЗОВЫ. Отклик отмечает то, что СЛУЧИЛОСЬ,
 * а не то, что человек и так видит. Поэтому его нет на прокрутке, открытии
 * экранов и обычных нажатиях: телефон, дрожащий на каждое касание, выключают
 * целиком — вместе с полезными местами.
 *
 * ⚠️ ИСКЛЮЧЕНИЕ (решение владельца 24.09.2026, сборка 61, D69): самый слабый
 * отклик `tapSelection` — на четырёх вкладках нижнего меню (`AppShell`) и на
 * ГЛАВНЫХ кнопках (`Button` с variant="primary": «Начать», «Проверить»,
 * «Отправить», «Войти»). Это то, чем приложение отличается от сайта в руке.
 * Второстепенные кнопки, ссылки, строки списков и прокрутка — по-прежнему без
 * отклика, по причине выше; добавлять туда — только новым решением владельца.
 * Выключатель в профиле гасит и это.
 *
 * РИСУНКИ НЕ ПРИДУМЫВАЮТСЯ. Система воспроизводит свой набор, одинаковый во всех
 * приложениях, поэтому «успех» и «ошибка» узнаются без объяснений. Мы лишь
 * выбираем, какой из них уместен.
 */

const STORAGE_KEY = 'onebilim:haptics';

/**
 * Выключатель из профиля. Часть людей не переносит вибрацию, и отказ должен
 * стоить один тап, а не поход в системные настройки телефона.
 *
 * Читаем синхронно из localStorage: отклик срабатывает в обработчиках нажатий,
 * и ждать асинхронное хранилище здесь нельзя — вибрация опоздает и потеряет
 * смысл. Когда токены переедут в защищённое хранилище (задача П32), эта
 * настройка останется здесь: она не секрет.
 */
export function hapticsEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function setHapticsEnabled(on: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off');
  } catch {
    // Приватный режим браузера — молча остаёмся на значении по умолчанию.
  }
}

/**
 * Вызывать отклик имеет смысл только в приложении из магазина: в браузере
 * `Haptics` либо промахивается мимо системы, либо на iOS не делает ничего.
 * Ошибки глушим — отклик никогда не должен ломать сценарий, ради которого
 * его позвали.
 */
function guard(run: () => Promise<unknown>): void {
  if (!isStoreBuild() || !hapticsEnabled()) return;
  void run().catch(() => {
    /* устройство без вибромотора или отказ системы — не наша забота */
  });
}

/** Едва заметный щелчок: палец переключил вариант, сменил карточку. */
export function tapSelection(): void {
  guard(() => Haptics.selectionChanged());
}

/** Короткое касание: мелкое подтверждение, отправленная реакция. */
export function tapLight(): void {
  guard(() => Haptics.impact({ style: ImpactStyle.Light }));
}

/** Ощутимый толчок: действие совершено — пошла запись, принят ответ. */
export function tapMedium(): void {
  guard(() => Haptics.impact({ style: ImpactStyle.Medium }));
}

/**
 * Тяжёлый толчок для крупных событий: сдача теста, удаление аккаунта.
 * Беречь — если он на каждом шагу, то перестаёт что-либо значить.
 */
export function tapHeavy(): void {
  guard(() => Haptics.impact({ style: ImpactStyle.Heavy }));
}

/** Два импульса по возрастающей — «получилось». */
export function notifySuccess(): void {
  guard(() => Haptics.notification({ type: NotificationType.Success }));
}

/** Два импульса по убыванию — «обрати внимание»: время на исходе. */
export function notifyWarning(): void {
  guard(() => Haptics.notification({ type: NotificationType.Warning }));
}

/** Три импульса — «не вышло». Узнаётся, даже когда телефон в кармане. */
export function notifyError(): void {
  guard(() => Haptics.notification({ type: NotificationType.Error }));
}

/**
 * Ответ проверен — самый частый случай во всём приложении, поэтому вынесен
 * отдельно: так на местах вызова читается намерение, а не выбор рисунка.
 */
export function answerChecked(correct: boolean): void {
  if (correct) notifySuccess();
  else notifyError();
}
